import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { submitFeedback } from '../lib/api';
import { getRecentPaths } from '../hooks/useJourneyTracking';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Heart, Lightbulb, AlertTriangle, HelpCircle, X, MessageCircleHeart, Check } from 'lucide-react';

const CATEGORIES = [
  { key: 'ros', icon: Heart, color: '#228B22' },
  { key: 'forslag', icon: Lightbulb, color: '#F5B800' },
  { key: 'feil', icon: AlertTriangle, color: '#E52B50' },
  { key: 'sporsmal', icon: HelpCircle, color: '#4A90D9' },
];

const FeedbackWidget = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const needsEmail = !user || !user.email;

  const resetForm = () => {
    setCategory('');
    setMessage('');
    setEmail('');
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(resetForm, 300);
  };

  const handleSubmit = async () => {
    if (!category || !message.trim()) return;
    setSubmitting(true);
    setError('');

    try {
      const browserInfo = `${navigator.userAgent}`;
      const viewportInfo = `${window.innerWidth}x${window.innerHeight}`;

      await submitFeedback({
        category,
        message: message.trim(),
        source_type: 'feedback_widget',
        page_url: window.location.href,
        page_path: window.location.pathname,
        page_title: document.title,
        last_10_paths_json: JSON.stringify(getRecentPaths()),
        user_journey_json: JSON.stringify(getRecentPaths()),
        email: needsEmail ? email : undefined,
        browser_info: browserInfo,
        viewport_info: viewportInfo,
        referrer: document.referrer || '',
        metadata_json: JSON.stringify({ language }),
      });

      setSuccess(true);
    } catch {
      setError(t('feedback.errorMessage'));
    } finally {
      setSubmitting(false);
    }
  };

  const categoryLabels = {
    ros: { label: t('feedback.categoryRos'), desc: t('feedback.categoryRosDesc') },
    forslag: { label: t('feedback.categoryForslag'), desc: t('feedback.categoryForslagDesc') },
    feil: { label: t('feedback.categoryFeil'), desc: t('feedback.categoryFeilDesc') },
    sporsmal: { label: t('feedback.categorySporsmal'), desc: t('feedback.categorySporsmalDesc') },
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 hidden md:flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#228B22] text-white font-medium text-sm shadow-lg hover:bg-[#1a6b1a] hover:shadow-xl transition-all duration-200 hover:scale-105"
        aria-label={t('feedback.widgetTab')}
      >
        <MessageCircleHeart className="h-4 w-4" />
        {t('feedback.widgetTab')}
      </button>

      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-40 md:hidden flex items-center justify-center w-12 h-12 rounded-full bg-[#228B22] text-white shadow-lg hover:bg-[#1a6b1a] transition-all duration-200 active:scale-95"
        aria-label={t('feedback.widgetTab')}
      >
        <MessageCircleHeart className="h-5 w-5" />
      </button>

      <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else setOpen(true); }}>
        <DialogContent className="sm:max-w-md rounded-2xl bg-[#FAF0E6] border-[#228B22]/20 p-0 max-h-[85vh] overflow-y-auto [&>button:last-child]:hidden">
          <div className="sticky top-0 z-10 bg-[#FAF0E6] px-6 pt-6 pb-4 flex items-center justify-between border-b border-[#228B22]/10">
            <h2 className="text-lg font-semibold text-[#2D2D2D]">{t('feedback.modalTitle')}</h2>
            <button onClick={handleClose} aria-label="Close" className="text-[#2D2D2D]/40 hover:text-[#2D2D2D]/70 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="px-6 py-5">
            {success ? (
              <div className="text-center py-6">
                <div className="mx-auto w-14 h-14 rounded-full bg-[#228B22]/10 flex items-center justify-center mb-4">
                  <Check className="h-7 w-7 text-[#228B22]" />
                </div>
                <h3 className="text-xl font-semibold text-[#228B22] mb-2">{t('feedback.successTitle')}</h3>
                <p className="text-sm text-[#2D2D2D]/70 mb-6 max-w-xs mx-auto">{t('feedback.successMessage')}</p>
                <Button
                  onClick={handleClose}
                  className="bg-[#228B22] hover:bg-[#1a6b1a] text-white rounded-full px-6"
                >
                  {t('feedback.successClose')}
                </Button>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-3">
                  {CATEGORIES.map(({ key, icon: Icon, color }) => (
                    <button
                      key={key}
                      onClick={() => setCategory(key)}
                      aria-pressed={category === key}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all duration-150 text-left ${
                        category === key
                          ? 'border-[#228B22] bg-[#228B22]/5 shadow-sm'
                          : 'border-[#228B22]/10 bg-white hover:border-[#228B22]/30 hover:bg-white/80'
                      }`}
                    >
                      <Icon className="h-5 w-5" style={{ color }} />
                      <span className="text-sm font-medium text-[#2D2D2D]">{categoryLabels[key].label}</span>
                      <span className="text-xs text-[#2D2D2D]/50 text-center leading-tight">{categoryLabels[key].desc}</span>
                    </button>
                  ))}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1.5">{t('feedback.messageLabel')}</label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t('feedback.messagePlaceholder')}
                    className="bg-white border-[#228B22]/15 focus:border-[#228B22] focus:ring-[#228B22]/20 rounded-xl resize-none min-h-[100px]"
                    rows={4}
                  />
                </div>

                {needsEmail && (
                  <div>
                    <label className="block text-sm font-medium text-[#2D2D2D] mb-1.5">{t('feedback.emailLabel')}</label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('feedback.emailPlaceholder')}
                      className="bg-white border-[#228B22]/15 focus:border-[#228B22] focus:ring-[#228B22]/20 rounded-xl"
                    />
                  </div>
                )}

                {error && (
                  <p className="text-sm text-[#E52B50]">{error}</p>
                )}

                <Button
                  onClick={handleSubmit}
                  disabled={!category || !message.trim() || submitting}
                  className="w-full bg-[#228B22] hover:bg-[#1a6b1a] text-white rounded-full py-2.5 font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  {submitting ? t('feedback.submitting') : t('feedback.submitButton')}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackWidget;
